import { CheckCircle2, CircleSlash, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { RoutingFacts } from '@/services/api/cases'

interface RoutingReasoningProps {
  facts: RoutingFacts
}

const STATUS_META: Record<string, { i18nKey: string; icon: typeof CheckCircle2; cls: string }> = {
  completed: {
    i18nKey: 'documents.detail.routing.statusCompleted',
    icon: CheckCircle2,
    cls: 'text-success',
  },
  failed: {
    i18nKey: 'documents.detail.routing.statusFailed',
    icon: XCircle,
    cls: 'text-destructive',
  },
  skipped: {
    i18nKey: 'documents.detail.routing.statusSkipped',
    icon: CircleSlash,
    cls: 'text-muted-foreground',
  },
}

/** Some analyzers stuff the entire analysis payload (document_type, file_type,
 *  confidence, summary, …) into the `summary` field as a JSON string instead of
 *  surfacing those values at the top level of `routing_facts`. Parse the blob
 *  once and return the extracted fields so callers can pick what they need.
 *  Returns null when the input isn't JSON-shaped or parsing fails. */
function parseEmbeddedAnalysis(raw: string | null | undefined): {
  summary: string | null
  document_type: string | null
  file_type: string | null
} | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed)
    return {
      summary:
        typeof parsed.summary === 'string' && parsed.summary.trim() ? parsed.summary.trim() : null,
      document_type: typeof parsed.document_type === 'string' ? parsed.document_type : null,
      file_type: typeof parsed.file_type === 'string' ? parsed.file_type : null,
    }
  } catch {
    return null
  }
}

/** Backwards-compatible wrapper kept for the summary-only call site below. */
function extractSummary(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  const embedded = parseEmbeddedAnalysis(trimmed)
  if (embedded?.summary) return embedded.summary
  return trimmed
}

function prettyValue(
  value: string | null | undefined,
  translatedMap: Record<string, string>
): string {
  if (!value) return '—'
  return translatedMap[value] ?? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function RoutingReasoning({ facts }: RoutingReasoningProps) {
  const { t } = useTranslation()

  const statusMeta = facts.analysis_status ? STATUS_META[facts.analysis_status] : null
  const StatusIcon = statusMeta?.icon
  const confidence = facts.analysis_confidence
  const confidencePct = typeof confidence === 'number' ? Math.round(confidence * 100) : null

  const embedded = parseEmbeddedAnalysis(facts.summary)
  const summary = extractSummary(facts.summary)
  const keyFindings = facts.key_findings ?? []

  // Fall back to values embedded in the summary JSON blob when the top-level
  // fact is missing or the literal "unknown" (some analyzer paths emit the
  // payload only as a JSON-encoded summary string).
  const effectiveDocumentType =
    (facts.document_type && facts.document_type !== 'unknown' ? facts.document_type : null) ??
    embedded?.document_type ??
    facts.document_type ??
    null
  const effectiveFileType =
    (facts.file_type && facts.file_type !== 'unknown' ? facts.file_type : null) ??
    embedded?.file_type ??
    facts.file_type ??
    null

  const fileTypeMap = t('documents.detail.routing.fileTypeValues', {
    returnObjects: true,
  }) as Record<string, string>
  const docTypeMap = t('documents.detail.routing.documentTypeValues', {
    returnObjects: true,
  }) as Record<string, string>

  return (
    <div className="space-y-4">
      {statusMeta && StatusIcon && (
        <div className="flex items-center gap-2">
          <StatusIcon size={14} className={statusMeta.cls} />
          <span className="text-xs font-medium text-foreground">{t(statusMeta.i18nKey)}</span>
          {confidencePct !== null && (
            <span className="ml-auto text-xs text-muted-foreground">
              {t('documents.detail.routing.confidence', { percent: confidencePct })}
            </span>
          )}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {effectiveFileType && (
          <Field
            label={t('documents.detail.routing.fileType')}
            value={prettyValue(effectiveFileType, fileTypeMap)}
          />
        )}
        {effectiveDocumentType && (
          <Field
            label={t('documents.detail.routing.documentType')}
            value={prettyValue(effectiveDocumentType, docTypeMap)}
          />
        )}
        {facts.extension && (
          <Field
            label={t('documents.detail.routing.extension')}
            value={facts.extension.toLowerCase()}
          />
        )}
        {typeof facts.pdf_text_density === 'number' && (
          <Field
            label={t('documents.detail.routing.textDensity')}
            value={t('documents.detail.routing.charsPerPage', {
              count: Math.round(facts.pdf_text_density),
            })}
          />
        )}
      </dl>

      {summary && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('documents.detail.routing.summary')}
          </div>
          <p className="text-xs leading-relaxed text-foreground">{summary}</p>
        </div>
      )}

      {keyFindings.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('documents.detail.routing.keyFindings')}
          </div>
          <ul className="list-inside list-disc space-y-0.5 text-xs leading-relaxed text-foreground">
            {keyFindings.map((finding, i) => (
              <li key={i}>{finding}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  )
}
